/**
 * @file SearchHelp.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-09-29
 * @description The "?" beside a search box's SEARCH label: how search works,
 *              and what this screen's search looks in.
 *
 *              Shown the moment the pointer (or keyboard focus) arrives — a
 *              help icon that makes you wait is one people stop trying. Drawn
 *              in a portal at a fixed position, so no filter bar or scrolling
 *              table can clip it.
 *
 *              The wording describes the one rule in utils/searchMatch.js and,
 *              on the server, utils/searchTerms.js. Change them together.
 *
 *              <label>Search <SearchHelp fields={['customer', 'order no.']} amounts /></label>
 */

import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SearchHelp.css';

function SearchHelp({ fields = [], amounts = false }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    // Below the icon, kept on screen at the right-hand edge.
    const width = 300;
    const left = Math.max(8, Math.min(r.left - 12, window.innerWidth - width - 8));
    setPos({ top: r.bottom + 8, left, width });
  };
  const hide = () => setPos(null);

  return (
    <>
      <span
        ref={ref}
        className="search-help"
        tabIndex={0}
        role="img"
        aria-label="How search works"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        // Inside a <label>: a click would otherwise focus the input and hide it.
        onClick={(e) => e.preventDefault()}
      >
        <span className="material-symbols-rounded">help</span>
      </span>
      {pos && createPortal(
        <div className="search-help-tip" role="tooltip" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          <div className="sht-title">How search works</div>
          <ul>
            <li>Type any words — results contain <strong>all</strong> of them, in any order.</li>
            <li>Part-words work (<em>burc</em> finds Burcombe) and capitals don&rsquo;t matter.</li>
            <li>Put <strong>&ldquo;quotes&rdquo;</strong> round an exact phrase: <em>&ldquo;W J Bailey&rdquo;</em>.</li>
            {amounts && <li>Amounts: <em>1234.50</em>, <em>1,234.50</em> or <em>£1,234.50</em>. A whole number finds the pounds: <em>335</em> finds £335.00–£335.99.</li>}
          </ul>
          {fields.length > 0 && (
            <div className="sht-fields"><span>Searches:</span> {fields.join(', ')}</div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

export default SearchHelp;
